import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { firestore, auth } from '../../database/firebase';
import {
  HiHeart,
  HiArrowPath,
  HiCheckCircle,
  HiXCircle,
  HiServer,
  HiCircleStack,
  HiBolt,
  HiSignal,
} from 'react-icons/hi2';

const CORE_COLLECTIONS = [
  'users',
  'clients',
  'organizations',
  'sessions',
  'userActivity',
  'security_logs',
  'otps',
  'programs',
  'messages',
  'documentBank',
  'knowledgeBase',
  'templates',
];

const DevSystemHealthPage = () => {
  const [stats, setStats] = useState({});
  const [firebaseOk, setFirebaseOk] = useState(null);
  const [authOk, setAuthOk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(null);

  const runHealthCheck = async () => {
    setLoading(true);
    const results = {};
    let firebaseStatus = false;
    let authStatus = false;

    try {
      for (const col of CORE_COLLECTIONS) {
        try {
          const snap = await getDocs(collection(firestore, col));
          results[col] = { count: snap.size, status: 'ok' };
        } catch (err) {
          results[col] = { count: 0, status: 'error', error: err?.message };
        }
      }
      firebaseStatus = true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Firestore check failed:', err);
    }

    try {
      authStatus = !!auth;
    } catch {
      authStatus = false;
    }

    setStats(results);
    setFirebaseOk(firebaseStatus);
    setAuthOk(authStatus);
    setLastCheck(new Date());
    setLoading(false);
  };

  useEffect(() => {
    runHealthCheck();
  }, []);

  const totalDocs = Object.values(stats).reduce((s, v) => s + (v?.count || 0), 0);
  const errorCount = Object.values(stats).filter(v => v?.status === 'error').length;
  const healthyCount = Object.values(stats).filter(v => v?.status === 'ok').length;

  return (
    <div className="flex flex-col gap-6">
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/25">
            <HiHeart className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">System Health</h1>
            <p className="text-sm text-slate-400">
              Firestore connectivity, collection stats, and service status
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastCheck && (
            <span className="text-xs text-slate-500">
              Last check: {lastCheck.toLocaleTimeString()}
            </span>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={runHealthCheck}
            disabled={loading}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-all hover:border-emerald-500/50 hover:bg-slate-700 disabled:opacity-60"
          >
            <HiArrowPath className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Run Check
          </motion.button>
        </div>
      </motion.header>

      {/* Status Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4"
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-lg ${firebaseOk ? 'bg-emerald-900/50' : 'bg-red-900/50'}`}
            >
              <HiServer className={`h-5 w-5 ${firebaseOk ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-500">Firestore</p>
              <p className={`font-semibold ${firebaseOk ? 'text-emerald-400' : 'text-red-400'}`}>
                {firebaseOk ? 'Connected' : 'Error'}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4"
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-lg ${authOk ? 'bg-emerald-900/50' : 'bg-slate-700'}`}
            >
              <HiBolt className={`h-5 w-5 ${authOk ? 'text-emerald-400' : 'text-slate-500'}`} />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-500">Auth</p>
              <p className={`font-semibold ${authOk ? 'text-emerald-400' : 'text-slate-400'}`}>
                {authOk ? 'Ready' : '—'}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-900/50">
              <HiCircleStack className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-500">Collections</p>
              <p className="font-semibold text-white">
                {healthyCount} / {CORE_COLLECTIONS.length}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-900/50">
              <HiSignal className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-slate-500">Total Docs</p>
              <p className="font-semibold text-white">{totalDocs.toLocaleString()}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Collection Status Grid */}
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
          <HiCircleStack className="h-5 w-5 text-slate-400" />
          Collection Status
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {CORE_COLLECTIONS.map((col, i) => {
            const s = stats[col];
            const ok = s?.status === 'ok';
            return (
              <motion.div
                key={col}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 + i * 0.02 }}
                className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
                  ok ? 'border-slate-700/50 bg-slate-800/40' : 'border-red-900/50 bg-red-950/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  {ok ? (
                    <HiCheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                  ) : (
                    <HiXCircle className="h-5 w-5 shrink-0 text-red-500" />
                  )}
                  <div>
                    <p className="font-medium text-slate-200">{col}</p>
                    <p className="text-xs text-slate-500">
                      {ok ? `${(s?.count || 0).toLocaleString()} docs` : s?.error || 'Error'}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {errorCount > 0 && (
        <div className="rounded-lg border border-amber-900/50 bg-amber-950/20 p-4">
          <p className="text-sm font-medium text-amber-300">
            {errorCount} collection{errorCount !== 1 ? 's' : ''} failed health check. This may be
            due to missing Firestore indexes or permissions.
          </p>
        </div>
      )}
    </div>
  );
};

export default DevSystemHealthPage;
